// SVG face renderer (face templates spec section 4). Draws a resolved face: one SVG per face with
// viewBox in face units. The SVG is decorative (aria-hidden); the brick's control carries the
// object's label. Text fitting measures with a cached canvas context.

import { memo, useEffect, useId, useState } from 'react';
import { SvgImage } from '../art/SvgImage';
import { atlasView } from './atlasView.ts';
import type { ResolvedFace, ResolvedIcon, ResolvedLayer, ResolvedPaint, ResolvedRichNode } from './resolve.ts';
import type { RichStyle } from './richText.ts';
import type { TextEffect } from './types.ts';
import { glyphPath } from '../presentation/Glyph';
import { textRole } from '../theme/themeV2.ts';
import type { ThemeV2 } from '../presentation/types.ts';

// ---- Text measurement -----------------------------------------------------------------

let canvas: CanvasRenderingContext2D | null | undefined;
const widths = new Map<string, number>();

/** Forgets measured widths (call once web fonts have loaded, so text fits with the real faces). */
export function clearMeasureCache(): void { widths.clear(); }

/** Width of `text` in face units at `size`, measured once per font, size and string. */
export function measure(text: string, font: string, size: number): number {
  const key = `${font}|${size}|${text}`;
  const hit = widths.get(key);
  if (hit !== undefined) return hit;
  if (canvas === undefined) canvas = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
  let w: number;
  if (canvas) {
    // Measure at 100 px for precision, then scale; family names resolve against the page.
    canvas.font = font.replace('SIZE', '100px');
    w = (canvas.measureText(text).width / 100) * size;
  } else w = text.length * size * 0.55;
  if (widths.size > 20000) widths.clear();
  widths.set(key, w);
  return w;
}

function resolveFamily(family: string): string {
  if (!family.startsWith('var(') || typeof document === 'undefined') return family;
  const name = family.slice(4, -1);
  const scope = document.querySelector('.scene-root') ?? document.documentElement;
  return getComputedStyle(scope).getPropertyValue(name).trim() || 'sans-serif';
}

/** Splits text into lines that fit `width`, at most `maxLines`; the last line ends with an ellipsis if cut. */
export function wrap(text: string, font: string, size: number, width: number, maxLines: number): { lines: string[]; clamped: boolean } {
  // A printed identifier can contain no spaces. Break only oversized words so
  // wrap never relies on clipping an entire long word outside its face box.
  const words = text.split(/\s+/).filter(Boolean).flatMap(word => {
    if (measure(word, font, size) <= width) return [word];
    const chunks: string[] = []; let chunk = '';
    for (const character of Array.from(word)) {
      if (chunk && measure(chunk + character, font, size) > width) { chunks.push(chunk); chunk = ''; }
      chunk += character;
    }
    if (chunk) chunks.push(chunk);
    return chunks;
  });
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (measure(next, font, size) <= width || !line) line = next;
    else { lines.push(line); line = word; }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return { lines, clamped: false };
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1]!;
  while (last.length > 1 && measure(last + '…', font, size) > width) last = last.slice(0, -1);
  kept[maxLines - 1] = last.trimEnd() + '…';
  return { lines: kept, clamped: true };
}

// ---- Paint, masks and shapes -------------------------------------------------------------

function paintRef(paint: ResolvedPaint | null, defs: React.ReactNode[], id: string, fallback = 'none'): string {
  if (!paint) return fallback;
  if (paint.kind === 'color') return paint.value;
  if (paint.kind === 'linear') {
    const a = ((paint.angle - 90) * Math.PI) / 180;
    const x = Math.cos(a) / 2, y = Math.sin(a) / 2;
    defs.push(
      <linearGradient key={id} id={id} x1={0.5 - x} y1={0.5 - y} x2={0.5 + x} y2={0.5 + y}>
        {paint.stops.map(([c, o], i) => <stop key={i} offset={o} stopColor={c} />)}
      </linearGradient>,
    );
  } else {
    defs.push(
      <radialGradient key={id} id={id} cx={paint.at[0] / 100} cy={paint.at[1] / 100} r={0.75}>
        {paint.stops.map(([c, o], i) => <stop key={i} offset={o} stopColor={c} />)}
      </radialGradient>,
    );
  }
  return `url(#${id})`;
}

export function shapePath(shape: string, x: number, y: number, w: number, h: number, r = 0): string {
  const cx = x + w / 2, cy = y + h / 2;
  switch (shape) {
    case 'circle': return `M${cx - w / 2},${cy} a${w / 2},${h / 2} 0 1,0 ${w},0 a${w / 2},${h / 2} 0 1,0 ${-w},0z`;
    case 'hex': return `M${x + w * 0.25},${y} h${w * 0.5} l${w * 0.25},${h / 2} l${-w * 0.25},${h / 2} h${-w * 0.5} l${-w * 0.25},${-h / 2}z`;
    case 'diamond': return `M${cx},${y} L${x + w},${cy} L${cx},${y + h} L${x},${cy}z`;
    case 'arch': return `M${x},${y + h} V${y + w / 2} a${w / 2},${w / 2} 0 0,1 ${w},0 V${y + h}z`;
    case 'shield': return `M${x},${y} H${x + w} V${y + h * 0.55} Q${x + w},${y + h * 0.85} ${cx},${y + h} Q${x},${y + h * 0.85} ${x},${y + h * 0.55}z`;
    case 'banner': return `M${x},${y} H${x + w} V${y + h} L${cx},${y + h * 0.78} L${x},${y + h}z`;
    case 'ribbon': return `M${x},${y} H${x + w} L${x + w - h * 0.35},${cy} L${x + w},${y + h} H${x} L${x + h * 0.35},${cy}z`;
    case 'pill': return roundRect(x, y, w, h, Math.min(w, h) / 2);
    case 'star': {
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? 0.45 : 1;
        pts.push(`${cx + Math.cos(a) * (w / 2) * rr},${cy + Math.sin(a) * (h / 2) * rr}`);
      }
      return `M${pts.join(' L')}z`;
    }
    default: return roundRect(x, y, w, h, r);
  }
}

function roundRect(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  if (rr === 0) return `M${x},${y} h${w} v${h} h${-w}z`;
  return `M${x + rr},${y} h${w - 2 * rr} a${rr},${rr} 0 0 1 ${rr},${rr} v${h - 2 * rr} a${rr},${rr} 0 0 1 ${-rr},${rr} h${-(w - 2 * rr)} a${rr},${rr} 0 0 1 ${-rr},${-rr} v${-(h - 2 * rr)} a${rr},${rr} 0 0 1 ${rr},${-rr}z`;
}

function patternDef(id: string, pattern: string, color: string, scale: number): React.ReactNode {
  const s = 4 * scale;
  switch (pattern) {
    case 'stripes': return <pattern key={id} id={id} width={s} height={s} patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width={s / 2} height={s} fill={color} /></pattern>;
    case 'dots': return <pattern key={id} id={id} width={s} height={s} patternUnits="userSpaceOnUse"><circle cx={s / 2} cy={s / 2} r={s / 5} fill={color} /></pattern>;
    case 'grid': return <pattern key={id} id={id} width={s} height={s} patternUnits="userSpaceOnUse"><path d={`M${s},0 L0,0 0,${s}`} fill="none" stroke={color} strokeWidth={s / 10} /></pattern>;
    case 'crosshatch': return <pattern key={id} id={id} width={s} height={s} patternUnits="userSpaceOnUse"><path d={`M0,0 L${s},${s} M${s},0 L0,${s}`} stroke={color} strokeWidth={s / 12} /></pattern>;
    case 'linen': return <pattern key={id} id={id} width={s / 2} height={s / 2} patternUnits="userSpaceOnUse"><path d={`M0,0 H${s / 2} M0,0 V${s / 2}`} stroke={color} strokeWidth={s / 16} /></pattern>;
    case 'wood': return <pattern key={id} id={id} width={s * 6} height={s} patternUnits="userSpaceOnUse"><path d={`M0,${s * 0.3} Q${s * 1.5},${s * 0.1} ${s * 3},${s * 0.35} T${s * 6},${s * 0.3}`} fill="none" stroke={color} strokeWidth={s / 10} /></pattern>;
    default: return (
      <filter key={id} id={id}><feTurbulence type="fractalNoise" baseFrequency={0.9 / scale} numOctaves={2} /><feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.35 0" /><feComposite in2="SourceGraphic" operator="in" /></filter>
    );
  }
}

function effectsFilter(effects: TextEffect[], id: string, defs: React.ReactNode[]): { filter?: string; stroke?: { color: string; width: number }; gradient?: string } {
  const out: { filter?: string; stroke?: { color: string; width: number }; gradient?: string } = {};
  const parts: React.ReactNode[] = [];
  let blur = 0;
  for (const [i, e] of effects.entries()) {
    if ('outline' in e) out.stroke = { color: e.outline.color, width: e.outline.width };
    else if ('shadow' in e) { blur += e.shadow.blur; parts.push(<feDropShadow key={i} dx={e.shadow.dx} dy={e.shadow.dy} stdDeviation={Math.min(e.shadow.blur, 2)} floodColor={e.shadow.color} />); }
    else if ('glow' in e) { blur += e.glow.blur; parts.push(<feDropShadow key={i} dx={0} dy={0} stdDeviation={Math.min(e.glow.blur, 2)} floodColor={e.glow.color} />); }
    else if ('emboss' in e) parts.push(<feDropShadow key={i} dx={0} dy={-e.emboss.depth / 4} stdDeviation={0.1} floodColor="#ffffff" floodOpacity={0.6} />);
    else if ('gradient' in e) {
      const gid = `${id}-g`;
      out.gradient = paintRef({ kind: 'linear', stops: e.gradient.linear.map((s, j, all) => (Array.isArray(s) ? s : [s, all.length === 1 ? 0 : j / (all.length - 1)])) as [string, number][], angle: e.gradient.angle ?? 180 }, defs, gid);
    }
  }
  // The total blur stays small so text remains legible.
  if (parts.length && blur <= 8) { defs.push(<filter key={id} id={id} x="-20%" y="-20%" width="140%" height="140%">{parts}</filter>); out.filter = `url(#${id})`; }
  return out;
}

// ---- Atlas frames -------------------------------------------------------------------------

type ImageLayer = Extract<ResolvedLayer, { type: 'image' }>;
type AtlasLayer = ImageLayer & { url: string; frame: NonNullable<ImageLayer['frame']>; sheet: NonNullable<ImageLayer['sheet']> };

/**
 * One atlas frame in a box: a nested SVG whose viewBox selects the frame (cropped around the focus
 * for cover), with the sheet clipped to the frame so neighbouring frames never show.
 */
function atlasImage(layer: AtlasLayer, b: { x: number; y: number; w: number; h: number }, id: string): React.ReactNode {
  const f = layer.frame;
  const { view: v, par } = atlasView(f, b, layer.fit as Parameters<typeof atlasView>[2], layer.focus);
  const view = [v.x, v.y, v.w, v.h];
  return (
    <svg x={b.x} y={b.y} width={b.w} height={b.h} viewBox={view.join(' ')} preserveAspectRatio={par} data-art="frame">
      <clipPath id={id}><rect x={f.x} y={f.y} width={f.w} height={f.h} /></clipPath>
      <SvgImage href={layer.url} width={layer.sheet.w} height={layer.sheet.h} clipPath={`url(#${id})`} />
    </svg>
  );
}

// ---- Layers ------------------------------------------------------------------------------

interface Ctx { w: number; h: number; radius: number; uid: string; theme: ThemeV2; defs: React.ReactNode[] }

function boxOf(layer: { box: [number, number, number, number] }, c: Ctx) {
  const [x1, y1, x2, y2] = layer.box;
  return { x: (x1 / 100) * c.w, y: (y1 / 100) * c.h, w: ((x2 - x1) / 100) * c.w, h: ((y2 - y1) / 100) * c.h };
}

function IconMark({ icon, x, y, size, fill }: { icon: ResolvedIcon; x: number; y: number; size: number; fill: string }) {
  if (icon.kind === 'image' && icon.frame && icon.sheet) return <svg x={x} y={y} width={size} height={size}
    viewBox={`${icon.frame.x} ${icon.frame.y} ${icon.frame.w} ${icon.frame.h}`} preserveAspectRatio="xMidYMid meet" overflow="hidden">
    <SvgImage href={icon.url} x={0} y={0} width={icon.sheet.w} height={icon.sheet.h} preserveAspectRatio="none" />
  </svg>;
  if (icon.kind === 'image') return <SvgImage href={icon.url} x={x} y={y} width={size} height={size} preserveAspectRatio="xMidYMid meet" />;
  const g = glyphPath(icon.name);
  const k = size / g.box;
  if (g.parts) return <g transform={`translate(${x} ${y}) scale(${k})`}>
    {g.parts.map((part, i) => <path key={i} d={part.d} fill={part.fill} stroke={part.stroke} strokeWidth={part.width} strokeLinecap="round" strokeLinejoin="round" />)}
  </g>;
  return (
    <path d={g.d} transform={`translate(${x} ${y}) scale(${k})`} fill={g.stroked ? 'none' : fill} stroke={g.stroked ? fill : 'none'}
      strokeWidth={g.stroked ? 2 : 0} strokeLinecap="round" strokeLinejoin="round" />
  );
}

type TextLayer = Extract<ResolvedLayer, { type: 'text' }>;
type RichPiece = { node: Exclude<ResolvedRichNode, { type: 'break' }>; width: number };

function richTextLayer(layer: TextLayer, c: Ctx, i: number): React.ReactNode {
  const bounds = boxOf(layer, c), [topPad, rightPad, bottomPad, leftPad] = layer.padding ?? [0, 0, 0, 0];
  const b = { x: bounds.x + leftPad, y: bounds.y + topPad, w: Math.max(0, bounds.w - leftPad - rightPad), h: Math.max(0, bounds.h - topPad - bottomPad) };
  if (b.w <= 0 || b.h <= 0) return null;
  const role = textRole(c.theme, layer.role);
  const baseSize = role.size * (layer.scale ?? 1);
  const font = (style: RichStyle) => ({
    family: resolveFamily(style.font ? `var(--font-${style.font})` : role.family),
    weight: style.bold ? 700 : role.weight,
    italic: style.italic || role.italic || layer.style === 'italic',
  });
  const layout = (size: number) => {
    const lines: RichPiece[][] = [[]]; let width = 0, pieces = 0, budgetClamped = false;
    const newline = () => { lines.push([]); width = 0; };
    const add = (node: RichPiece['node'], w: number) => {
      if (++pieces > 2048) { budgetClamped = true; return; }
      if (width && width + w > b.w) newline();
      lines.at(-1)!.push({ node, width: w }); width += w;
    };
    for (const node of layer.rich ?? []) {
      if (budgetClamped) break;
      if (node.type === 'break') { newline(); continue; }
      if (node.type === 'icon') {
        add(node, node.icon ? size : Math.max(size, measure(node.key, `500 SIZE ${resolveFamily(role.family)}`, size * 0.7)));
        continue;
      }
      const style = font(node.style);
      const fontSpec = `${style.italic ? 'italic ' : ''}${style.weight} SIZE ${resolveFamily(style.family)}`;
      const value = role.caps || layer.style === 'caps' ? node.text.toUpperCase() : node.text;
      for (const token of value.split(/(\r\n|\r|\n|[^\S\r\n]+)/).filter(Boolean)) {
        if (budgetClamped) break;
        if (/^[\r\n]/.test(token)) { newline(); continue; }
        const whitespace = /^\s+$/.test(token);
        if (whitespace && !width) continue;
        const tokenWidth = measure(token, fontSpec, size);
        if (whitespace && width + tokenWidth > b.w) { newline(); continue; }
        if (tokenWidth <= b.w || whitespace) { add({ ...node, text: token }, tokenWidth); continue; }
        // Break a single oversized identifier without placing it beyond the box.
        let chunk = '';
        for (const character of Array.from(token)) {
          if (budgetClamped) break;
          if (chunk && measure(chunk + character, fontSpec, size) > b.w) {
            add({ ...node, text: chunk }, measure(chunk, fontSpec, size)); chunk = '';
          }
          chunk += character;
        }
        if (chunk) add({ ...node, text: chunk }, measure(chunk, fontSpec, size));
      }
    }
    return { lines, budgetClamped };
  };
  let size = baseSize, result = layout(size);
  const factor = layer.lineHeight ?? 1.2;
  if (layer.fit === 'shrink') for (const ratio of [0.85, 0.6]) {
    if (result.lines.length * size * factor <= b.h) break;
    size = baseSize * ratio; result = layout(size);
  }
  const lineH = size * factor, maxLines = Math.max(0, Math.floor(b.h / lineH));
  const clamped = result.budgetClamped || result.lines.length > maxLines;
  const lines = result.lines.slice(0, maxLines);
  const total = lines.length * lineH;
  const y = layer.valign === 'top' ? b.y : layer.valign === 'bottom' ? b.y + b.h - total : b.y + (b.h - total) / 2;
  const fx = effectsFilter(layer.effects, `${c.uid}-fx${i}`, c.defs);
  const fill = fx.gradient ?? paintRef(layer.ink, c.defs, `${c.uid}-ink${i}`, 'currentColor');
  const clipId = `${c.uid}-richClip${i}`;
  c.defs.push(<clipPath key={clipId} id={clipId}><rect x={b.x} y={b.y} width={b.w} height={b.h} /></clipPath>);
  return <g clipPath={`url(#${clipId})`} data-rich-text="true" data-fit={(size / baseSize).toFixed(2)} data-clamped={clamped || undefined}>
    {lines.map((line, n) => {
      const lineWidth = line.reduce((sum, piece) => sum + piece.width, 0);
      let x = b.x + (layer.align === 'center' ? (b.w - lineWidth) / 2 : layer.align === 'end' ? b.w - lineWidth : 0);
      return <g key={n}>{line.map((piece, j) => {
        const start = x; x += piece.width;
        if (piece.node.type === 'icon') return piece.node.icon
          ? <IconMark key={j} icon={piece.node.icon} x={start} y={y + n * lineH} size={size} fill={piece.node.style.color ?? fill} />
          : <text key={j} data-icon-fallback={piece.node.key} x={start} y={y + n * lineH + size * 0.86} fontSize={size * 0.7} fill={fill} style={{ fontFamily: resolveFamily(role.family) }}>{piece.node.key}</text>;
        if (piece.node.type !== 'text') return null;
        const style = font(piece.node.style);
        return <text key={j} x={start} y={y + n * lineH + size * 0.86} fontSize={size} fontWeight={style.weight}
          fontStyle={style.italic ? 'italic' : 'normal'} style={{ fontFamily: style.family }}
          fill={piece.node.style.color ?? fill} filter={fx.filter} stroke={fx.stroke?.color} strokeWidth={fx.stroke?.width} paintOrder="stroke">{piece.node.text}</text>;
      })}</g>;
    })}
  </g>;
}

function textLayer(layer: TextLayer, c: Ctx, i: number): React.ReactNode {
  if (layer.rich) return richTextLayer(layer, c, i);
  const b = boxOf(layer, c);
  const base = textRole(c.theme, layer.role);
  const role = { ...base, size: base.size * (layer.scale ?? 1) };
  const italic = role.italic || layer.style === 'italic';
  const caps = role.caps || layer.style === 'caps';
  const text = caps ? layer.text.toUpperCase() : layer.text;
  const family = resolveFamily(role.family);
  const font = `${italic ? 'italic ' : ''}${role.weight} SIZE ${family}`;
  let size = role.size;
  let lines: string[];
  let clamped = false;
  if (layer.fit === 'shrink') {
    // Shrink a little on one line; past 85%, prefer two lines at 85% when the box is tall enough;
    // otherwise shrink to at most 60% and clamp.
    const natural = measure(text, font, size);
    lines = [text];
    if (natural > b.w) {
      const single = (size * b.w * 0.98) / natural;
      const wrapped = wrap(text, font, role.size * 0.85, b.w, Math.max(1, Math.floor(b.h / (role.size * 0.85 * 1.15))));
      if (single >= role.size * 0.85) size = single;
      else if (!wrapped.clamped && wrapped.lines.length > 1) { size = role.size * 0.85; lines = wrapped.lines; }
      else size = Math.max(role.size * 0.6, single);
    }
    if (lines.length === 1 && measure(text, font, size) > b.w * 1.001) ({ lines, clamped } = wrap(text, font, size, b.w, Math.max(1, Math.floor(b.h / (size * 1.15)))));
  } else {
    ({ lines, clamped } = wrap(text, font, size, b.w, layer.fit === 'clamp' ? Math.max(1, Math.floor(b.h / (size * 1.2))) : 99));
  }
  const lineH = size * 1.18;
  const total = lineH * lines.length;
  const top = layer.valign === 'top' ? b.y : layer.valign === 'bottom' ? b.y + b.h - total : b.y + (b.h - total) / 2;
  const x = layer.align === 'center' ? b.x + b.w / 2 : layer.align === 'end' ? b.x + b.w : b.x;
  const fx = effectsFilter(layer.effects, `${c.uid}-fx${i}`, c.defs);
  const fill = fx.gradient ?? paintRef(layer.ink, c.defs, `${c.uid}-ink${i}`, 'currentColor');
  return (
    <text x={x} style={{ fontFamily: family }} fontSize={size} fontWeight={role.weight} fontStyle={italic ? 'italic' : 'normal'} textAnchor={layer.align === 'center' ? 'middle' : layer.align === 'end' ? 'end' : 'start'}
      fill={fill} filter={fx.filter} stroke={fx.stroke?.color} strokeWidth={fx.stroke?.width} paintOrder="stroke" strokeLinejoin="round" clipPath={layer.fit === 'wrap' ? `url(#${c.uid}-clip${i})` : undefined}
      data-fit={(size / role.size).toFixed(2)} data-clamped={clamped || undefined}>
      {lines.map((line, n) => <tspan key={n} x={x} y={top + lineH * n + size * 0.86}>{line}</tspan>)}
    </text>
  );
}

function drawLayer(layer: ResolvedLayer, c: Ctx, i: number): React.ReactNode {
  const b = boxOf(layer, c);
  const whole = layer.box[0] === 0 && layer.box[1] === 0 && layer.box[2] === 100 && layer.box[3] === 100;
  switch (layer.type) {
    case 'fill':
      return <path d={roundRect(b.x, b.y, b.w, b.h, whole ? c.radius : 0)} fill={paintRef(layer.paint, c.defs, `${c.uid}-p${i}`)} />;
    case 'shape':
      return <path d={shapePath(layer.shape, b.x, b.y, b.w, b.h, Math.min(b.w, b.h) * 0.12)} fill={paintRef(layer.paint, c.defs, `${c.uid}-p${i}`)}
        stroke={layer.stroke ? paintRef(layer.stroke.paint, c.defs, `${c.uid}-s${i}`) : undefined} strokeWidth={layer.stroke?.width} />;
    case 'pattern': {
      const id = `${c.uid}-pat${i}`;
      const color = layer.paint?.kind === 'color' ? layer.paint.value : 'rgb(0 0 0 / 25%)';
      c.defs.push(patternDef(id, layer.pattern, color, layer.scale));
      return layer.pattern === 'noise'
        ? <rect x={b.x} y={b.y} width={b.w} height={b.h} fill={color} filter={`url(#${id})`} />
        : <path d={roundRect(b.x, b.y, b.w, b.h, whole ? c.radius : 0)} fill={`url(#${id})`} />;
    }
    case 'image': {
      if (layer.pattern) {
        const id = `${c.uid}-art${i}`;
        c.defs.push(patternDef(id, layer.pattern, 'rgb(0 0 0 / 22%)', 1));
        return layer.pattern === 'noise' ? <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="rgb(0 0 0 / 22%)" filter={`url(#${id})`} /> : <path d={roundRect(b.x, b.y, b.w, b.h, 0)} fill={`url(#${id})`} data-art="pattern" />;
      }
      if (!layer.url) return null;
      const image = layer.frame && layer.sheet ? atlasImage(layer as AtlasLayer, b, `${c.uid}-fr${i}`)
        : <SvgImage href={layer.url} placeholder={!layer.tint} x={b.x} y={b.y} width={b.w} height={b.h} preserveAspectRatio={layer.fit === 'contain' ? 'xMidYMid meet' : layer.fit === 'fill' ? 'none' : 'xMidYMid slice'} />;
      if (!layer.tint) return image;
      const tintId = `${c.uid}-tint${i}`;
      c.defs.push(<filter key={tintId} id={tintId} colorInterpolationFilters="sRGB"><feFlood floodColor={layer.tint} result="tint" /><feBlend in="SourceGraphic" in2="tint" mode="multiply" /><feComposite in2="SourceGraphic" operator="in" /></filter>);
      return <g filter={`url(#${tintId})`}>{image}</g>;
    }
    case 'text': return textLayer(layer, c, i);
    case 'icon': {
      const size = Math.min(b.w, b.h);
      return <IconMark icon={layer.icon} x={b.x + (b.w - size) / 2} y={b.y + (b.h - size) / 2} size={size} fill={layer.paint?.kind === 'color' ? layer.paint.value : 'currentColor'} />;
    }
    case 'icon-row': {
      const s = layer.iconSize;
      const role = textRole(c.theme, 'number');
      const tint = layer.paint?.kind === 'color' ? layer.paint.value : 'currentColor';
      const items: React.ReactNode[] = [];
      let x = b.x, y = b.y + (b.h - s) / 2;
      for (const [n, item] of layer.items.entries()) {
        const numeric = layer.style === 'symbols' && layer.numberKeys.includes(item.key);
        const repeated = layer.style === 'repeat' || (layer.style === 'symbols' && !numeric && item.value <= 9);
        const count = repeated ? Math.min(9, item.value) : 1;
        const width = numeric ? s : repeated ? count * (s + layer.gap / 2) : s * 1.9;
        if (x + width > b.x + b.w && x > b.x) { x = b.x; y += s + layer.gap; }
        if (numeric) {
          if (item.icon) items.push(<IconMark key={`${n}i`} icon={item.icon} x={x} y={y} size={s} fill={tint} />);
          items.push(<text key={`${n}t`} x={x + s / 2} y={y + s * .74} textAnchor="middle" fontSize={s * (String(item.value).length > 2 ? .48 : .7)} style={{ fontFamily: resolveFamily(role.family) }} fontWeight={role.weight} fill={tint}>{item.value}</text>);
        } else if (repeated) {
          for (let k = 0; k < count; k++) items.push(item.icon ? <IconMark key={`${n}-${k}`} icon={item.icon} x={x + k * (s + layer.gap / 2)} y={y} size={s} fill={tint} /> : null);
        } else {
          const numberFirst = layer.style === 'count-icon';
          const tx = numberFirst ? x : x + s + 0.4;
          items.push(<text key={`${n}t`} x={tx} y={y + s * 0.82} fontSize={s * 0.95} style={{ fontFamily: resolveFamily(role.family) }} fontWeight={role.weight} fill={tint}>{item.value}</text>);
          if (item.icon) items.push(<IconMark key={`${n}i`} icon={item.icon} x={numberFirst ? x + s * 0.7 : x} y={y} size={s} fill={tint} />);
        }
        x += width + layer.gap;
      }
      return <g>{items}</g>;
    }
    case 'badge': {
      const size = Math.min(b.w, b.h);
      const bx = b.x + (b.w - size) / 2, by = b.y + (b.h - size) / 2;
      const ink = paintRef(layer.ink, c.defs, `${c.uid}-bi${i}`, 'currentColor');
      const fx = effectsFilter(layer.effects, `${c.uid}-bfx${i}`, c.defs);
      const role = textRole(c.theme, 'number');
      const w = layer.shape === 'pill' || layer.shape === 'banner' ? b.w : size;
      return (
        <g>
          <path d={shapePath(layer.shape, layer.shape === 'pill' || layer.shape === 'banner' ? b.x : bx, by, w, size)} fill={paintRef(layer.paint, c.defs, `${c.uid}-bp${i}`, '#ffffff')} stroke="rgb(0 0 0 / 25%)" strokeWidth={size * 0.04} />
          {layer.icon && <IconMark icon={layer.icon} x={bx + size * 0.2} y={by + size * 0.2} size={size * 0.6} fill={layer.ink?.kind === 'color' ? layer.ink.value : 'currentColor'} />}
          {layer.text !== null && (
            <text x={b.x + b.w / 2} y={by + size * (layer.shape === 'shield' ? 0.62 : 0.68)} textAnchor="middle" fontSize={size * (layer.text.length > 2 ? 0.42 : 0.55)} style={{ fontFamily: resolveFamily(role.family) }}
              fontWeight={700} fill={ink} filter={fx.filter} stroke={fx.stroke?.color} strokeWidth={fx.stroke?.width} paintOrder="stroke">{layer.text}</text>
          )}
        </g>
      );
    }
    case 'frame': {
      const color = paintRef(layer.paint, c.defs, `${c.uid}-f${i}`, 'rgb(0 0 0 / 35%)');
      const w = layer.width, inset = w / 2;
      const r = whole ? Math.max(0, c.radius - inset) : 0;
      const outer = <path d={roundRect(b.x + inset, b.y + inset, b.w - w, b.h - w, r)} fill="none" stroke={color} strokeWidth={w} />;
      switch (layer.style) {
        case 'minimal': return <path d={roundRect(b.x + inset, b.y + inset, b.w - w, b.h - w, r)} fill="none" stroke={color} strokeWidth={w * 0.5} />;
        case 'double': return <g>{outer}<path d={roundRect(b.x + w * 2, b.y + w * 2, b.w - w * 4, b.h - w * 4, Math.max(0, r - w))} fill="none" stroke={color} strokeWidth={w * 0.5} /></g>;
        case 'tarot': return <g><path d={roundRect(b.x + w, b.y + w, b.w - 2 * w, b.h - 2 * w, r)} fill="none" stroke={color} strokeWidth={w * 2} /><path d={roundRect(b.x + w * 3, b.y + w * 3, b.w - w * 6, b.h - w * 6, 0)} fill="none" stroke={color} strokeWidth={w * 0.4} /></g>;
        case 'notched': {
          const n = Math.min(b.w, b.h) * 0.08;
          return <path d={`M${b.x + n},${b.y + inset} H${b.x + b.w - n} L${b.x + b.w - inset},${b.y + n} V${b.y + b.h - n} L${b.x + b.w - n},${b.y + b.h - inset} H${b.x + n} L${b.x + inset},${b.y + b.h - n} V${b.y + n}z`} fill="none" stroke={color} strokeWidth={w} />;
        }
        case 'ornate': {
          const d = Math.min(b.w, b.h) * 0.07;
          return (
            <g>{outer}
              <path d={roundRect(b.x + w * 2.2, b.y + w * 2.2, b.w - w * 4.4, b.h - w * 4.4, Math.max(0, r - w))} fill="none" stroke={color} strokeWidth={w * 0.4} />
              {[[b.x, b.y], [b.x + b.w, b.y], [b.x, b.y + b.h], [b.x + b.w, b.y + b.h]].map(([px, py], k) => (
                <path key={k} d={shapePath('diamond', px! - d / 2 + (px === b.x ? d * 0.9 : -d * 0.9), py! - d / 2 + (py === b.y ? d * 0.9 : -d * 0.9), d, d)} fill={color} />
              ))}
            </g>
          );
        }
        default: return outer;
      }
    }
    case 'slot': return null;
  }
}

function clipFor(mask: string, b: { x: number; y: number; w: number; h: number }, id: string, defs: React.ReactNode[]): string | undefined {
  if (mask === 'none' || mask.startsWith('art:')) return undefined;
  const d = mask === 'rounded' ? roundRect(b.x, b.y, b.w, b.h, Math.min(b.w, b.h) * 0.08) : shapePath(mask, b.x, b.y, b.w, b.h);
  defs.push(<clipPath key={id} id={id}><path d={d} /></clipPath>);
  return `url(#${id})`;
}

interface FaceProps { face: ResolvedFace; theme: ThemeV2; className?: string; preserveAspectRatio?: string }

/** One resolved face as an SVG. Memoized: the same resolved face object draws once. */
export const Face = memo(function Face({ face, theme, className, preserveAspectRatio }: FaceProps) {
  const [, refreshFonts] = useState(0);
  useEffect(() => {
    // Initial render can precede both the scene's scoped font variables and web
    // font delivery. Refit with the mounted scope and the actual loaded glyphs.
    let mounted = true;
    const refit = () => { clearMeasureCache(); if (mounted) refreshFonts(n => n + 1); };
    void document.fonts.ready.then(refit);
    document.fonts.addEventListener('loadingdone', refit);
    return () => { mounted = false; document.fonts.removeEventListener('loadingdone', refit); };
  }, []);
  const uid = useId().replace(/:/g, '');
  const defs: React.ReactNode[] = [];
  const c: Ctx = { w: face.size.w, h: face.size.h, radius: face.size.radius, uid, theme, defs };
  const body = face.layers.map((layer, i) => {
    const node = drawLayer(layer, c, i);
    const b = boxOf(layer, c);
    const clip = clipFor(layer.mask, b, `${uid}-m${i}`, defs);
    if (layer.type === 'text' && layer.fit === 'wrap') defs.push(<clipPath key={`${uid}-clip${i}`} id={`${uid}-clip${i}`}><rect x={b.x} y={b.y} width={b.w} height={b.h} /></clipPath>);
    const transform = layer.rotate ? `rotate(${layer.rotate} ${b.x + b.w / 2} ${b.y + b.h / 2})` : undefined;
    const style = layer.blend !== 'normal' ? ({ mixBlendMode: layer.blend } as React.CSSProperties) : undefined;
    return <g key={i} opacity={layer.opacity < 1 ? layer.opacity : undefined} clipPath={clip} transform={transform} style={style}>{node}</g>;
  });
  return (
    <svg className={className ?? 'face'} viewBox={`0 0 ${face.size.w} ${face.size.h}`} preserveAspectRatio={preserveAspectRatio} aria-hidden="true" focusable="false">
      <defs>
        <clipPath id={`${uid}-face`}><path d={roundRect(0, 0, face.size.w, face.size.h, face.size.radius)} /></clipPath>
      </defs>
      <g clipPath={`url(#${uid}-face)`}>{body}</g>
      <defs>{defs}</defs>
    </svg>
  );
});
